// Based on the thread id and user id
// This composable encapsulates the logic to get the thread values and next and previous thread values for a user_id

// import the threads store, to see if we have value cached
import { useThreadsContextStore } from "@/stores/threads";
import { useUserStore } from "@/stores/user";
import { useToast } from "vue-toastification";

import Thread from "@/api/threads";

export function useUserThreads(threadRef) {
  const toast = useToast();
  const userStore = useUserStore();
  const threadsContextStore = useThreadsContextStore();

  const threadIds = ref([]); // maintains the order
  const threads = ref({}); // ez lookup for actual thread details

  const isLoading = ref(false);
  const setIsLoading = (state) => {
    threadsContextStore.isLoading = state;
  };

  // pass initialize out so it only gets called once
  const initialize = async (thread) => {
    console.log("thread: ", thread);
    if (!thread._id) return;

    threadIds.value = [thread._id];

    const threadFromStore = threadsContextStore.getThreadById(thread._id);
    if (threadFromStore) {
      threads.value[thread._id] = threadFromStore;
      return;
    }

    try {
      setIsLoading(true);
      const thread = await Thread.getThread(thread._id);

      threads.value[thread._id] = thread;
      threadsContextStore.threads[thread._id] = thread;
      threadIds.value = [thread._id];
    } catch (err) {
      console.error(`Error in _getCurrentThread method. Error is: ${err}`);
    } finally {
      setIsLoading(false);
    }
  };

  // get previous thread
  const getPreviousthread = async () => {
    const oldestThreadId = threadIds.value[0];
    if (!oldestThreadId) return;

    const filters = {
      user_id: threads.value[threadRef.value._id].user_id,
      organization_id: userStore.activeOrganization,
      created_at_to: threads.value[oldestThreadId].created_at,
      limit: 2,
    };

    try {
      setIsLoading(true);
      const { data } = await Thread.getThreads(filters);

      if (!Array.isArray(data) || data.length < 2) {
        toast.warning("No older Conversations")
        return;
      };

      const [cur, prev] = data;
      threadIds.value.unshift(prev._id);
      threads.value[prev._id] = prev;

      threadsContextStore.threads[prev._id] = prev
    } catch (err) {
      console.error("Error getting previous thread. The error is: ", err);
    } finally {
      setIsLoading(false);
    }
  };

  // get previous thread
  const getNextThread = async () => {
    const newestThreadId = threadIds.value.at(-1);
    console.log("newestThreadId: ", newestThreadId);
    if (!newestThreadId) return;

    const filters = {
      user_id: threads.value[threadRef.value._id].user_id,
      organization_id: userStore.activeOrganization,
      created_at_from: threads.value[newestThreadId].created_at,
      order: "asc",
      limit: 2,
    };

    try {
      setIsLoading(true);
      const { data } = await Thread.getThreads(filters);

      if (!Array.isArray(data) || data.length < 2) {
        toast.warning("No new conversations")
        return;
      };

      const [cur, next] = data;
      threadIds.value.push(next._id);
      threads.value[next._id] = next;

      // add to store
      threadsContextStore.threads[next._id] = next
    } catch (err) {
      console.error("Error getting next thread. The error is: ", err);
    } finally {
      setIsLoading(false);
    }
  };

  // I want to load in the current thread data and nuke everything
  watch(threadRef, (newThread) => {
    
    // hack way of making sure it only initializes once...
    // normally I would pass the initalize method to the component and let the component handle it
    if(!threadIds.value.length) {
      initialize(newThread)
    }  
  
  }, { immediate: true });

  return {
    threadIds,
    threads,
    isLoading,
    getPreviousthread,
    getNextThread,
  };
}
